import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import packageJson from '../../package.json'
import AuthLayout from 'layouts/AuthLayout'
import { getOnChangeFunction, getFormattedMissingFieldsMessage } from 'utils'
import { useAuth } from 'auth/AuthContext'
import { makeRequest, ACCOUNT_DISABLED_MESSAGE } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { isSystemAdmin } from 'constants/roles'
import { SYSTEM_HOME } from 'auth/ProtectedRoute'

const Login = () => {
  const { login } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [credentials, setCredentials] = useState({ email: '', password: '' })
  const [error, setError] = useState('')

  const onChangeValue = getOnChangeFunction(setCredentials, credentials)

  const onSubmit = async (e) => {
    e.preventDefault()

    const htmlMessage = getFormattedMissingFieldsMessage(credentials, ['email', 'password'])
    if (htmlMessage !== '') {
      showAlert({
        title: t('login.missingFieldsTitle'),
        html: htmlMessage,
        icon: 'warning'
      })
      return
    }

    await loginRequest()
  }

  const loginRequest = async () => {
    try {
      const result = await makeRequest({
        method: 'post',
        url: '/login',
        data: credentials
      })

      if (!result || !result.data) {
        throw new Error('No data returned from server')
      }
      if (result.status === 200) {
        login(result.data)
        navigate(isSystemAdmin(result.data) ? SYSTEM_HOME : '/dashboard/last-working-day-summary')
      }
    } catch (error) {
      console.error('Login Error:', error)

      if (error.response) {
        const { status, data } = error.response
        if (status === 401 && data?.message === ACCOUNT_DISABLED_MESSAGE) {
          setError(t('login.accountDisabled'))
          return
        }
        if (status === 401 || status === 403) {
          setError(t('login.invalidCredentials'))
          return
        }
      }
      setError(t('login.genericError'))
    }
  }

  return (
    <AuthLayout>
      <h2 className='text-xl font-semibold text-greenPrimary mb-4'>{t('login.title')}</h2>
      <form className='w-full' onSubmit={onSubmit} autoComplete='off'>
        <div className='mb-4'>
          <label className='block text-gray-600 text-sm font-medium mb-1'>{t('login.emailLabel')}</label>
          <input
            type='email'
            name='email'
            placeholder={t('login.emailPlaceholder')}
            value={credentials.email}
            onChange={onChangeValue}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>
        <div className='mb-6'>
          <label className='block text-gray-600 text-sm font-medium mb-1'>{t('login.passwordLabel')}</label>
          <input
            type='password'
            name='password'
            placeholder={t('login.passwordPlaceholder')}
            value={credentials.password}
            onChange={onChangeValue}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>
        <button
          type='submit'
          className='w-full bg-greenPrimary text-white py-2 rounded-md hover:bg-green-900 transition'
        >
          {t('login.submit')}
        </button>
        {error && <p className='text-red-600 text-sm mt-2 text-center'>{error}</p>}
      </form>
      <div className='text-center mt-4 text-sm text-gray-600'>
        {t('login.noAccount')}{' '}
        <a href='/signup' className='text-greenPrimary font-semibold hover:underline'>
          {t('login.signUpHere')}
        </a>
      </div>
      <p className='text-xs text-center text-gray-400 mt-4'>
        {t('login.version', { version: packageJson.version })}
      </p>
    </AuthLayout>
  )
}

export default Login
